<script lang="ts">
	import { generateId } from '@repo/commons/utils'
	import { tick } from 'svelte'
	import type { UploadedImage } from '@repo/commons/models'
	import {
		validateImageFile,
		createImageMarkdown,
		createPreviewUrl,
		revokePreviewUrl,
		uploadImageToMinIO
	} from '../markdownInputUtils'

	interface ImageUploadManagerProps {
		enabled?: boolean
		acceptedTypes?: string[]
		maxSize?: number
		readonly?: boolean
		disabled?: boolean
		onImageInsert?: (markdown: string, cursorPosition: number) => void
		onImageRemove?: (filename: string, url: string) => void
		onUploadComplete?: (imageData: UploadedImage) => void
		onUploadError?: (error: string, filename: string) => void
	}

	let {
		enabled = true,
		acceptedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
		maxSize = 10,
		readonly = false,
		disabled = false,
		onImageInsert = () => {},
		onImageRemove = () => {},
		onUploadComplete = () => {},
		onUploadError = () => {}
	}: ImageUploadManagerProps = $props()

	let fileInputRef = $state<HTMLInputElement>()
	let uploadedImages = $state<UploadedImage[]>([])
	let previewUrls = $state<Map<string, string>>(new Map())

	// Expose uploaded images and methods
	export { uploadedImages }

	export function triggerFileSelect() {
		if (!enabled || readonly || disabled) return
		fileInputRef?.click()
	}

	export function removeImage(imageId: string) {
		const imageToRemove = uploadedImages.find((img) => img.id === imageId)
		if (imageToRemove) {
			// Notify parent to remove from markdown
			onImageRemove(imageToRemove.file.name, imageToRemove.url)

			// Clean up preview URL if it exists
			const previewUrl = previewUrls.get(imageId)
			if (previewUrl) {
				revokePreviewUrl(previewUrl)
				previewUrls.delete(imageId)
			}
		}

		// Remove from uploaded images
		uploadedImages = uploadedImages.filter((img) => img.id !== imageId)
	}

	export function retryUpload(imageId: string) {
		const imageToRetry = uploadedImages.find((img) => img.id === imageId)
		if (imageToRetry) {
			processImageFile(imageToRetry.file, imageId)
		}
	}

	export function processClipboardImage(file: File) {
		if (!enabled || readonly || disabled) return

		// Generate a filename for clipboard images
		const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
		const extension = file.type.split('/')[1] || 'png'
		const clipboardFile = new File([file], `clipboard-image-${timestamp}.${extension}`, {
			type: file.type
		})

		processImageFile(clipboardFile)
	}

	function generateFileName(file: File): string {
		const uuid = generateId()
		const extension = file.type.split('/')[1] || 'png'
		return `${uuid}.${extension}`
	}

	export function clearAllImages() {
		// Clean up all preview URLs
		previewUrls.forEach((url) => revokePreviewUrl(url))
		previewUrls.clear()
		uploadedImages = []
	}

	async function handleFileChange(event: Event) {
		const target = event.target as HTMLInputElement
		const files = target.files

		if (!files || files.length === 0) return

		for (const file of Array.from(files)) {
			await processImageFile(file)
		}

		target.value = ''
	}

	async function processImageFile(file: File, existingId?: string) {
		const validation = validateImageFile(file, acceptedTypes, maxSize)
		if (!validation.isValid) {
			onUploadError(validation.error || 'Invalid file', file.name)
			return
		}

		const previewUrl = createPreviewUrl(file)
		const tempId = existingId || generateId()

		// Update existing image or create new one
		if (existingId) {
			uploadedImages = uploadedImages.map((img) =>
				img.id === existingId
					? {
							...img,
							uploading: true,
							error: undefined
						}
					: img
			)
		} else {
			const imageData: UploadedImage = {
				id: tempId,
				url: previewUrl,
				file,
				uploading: true
			}

			uploadedImages = [...uploadedImages, imageData]
			previewUrls.set(tempId, previewUrl)

			const imageMarkdown = createImageMarkdown(generateFileName(file), previewUrl)
			onImageInsert(imageMarkdown, 0) // Parent will handle cursor position
		}

		await tick()

		try {
			const uploadResult = await uploadImageToMinIO(file)

			console.log('uploadResult', uploadResult)

			uploadedImages = uploadedImages.map((img) =>
				img.id === tempId
					? {
							...img,
							url: uploadResult.url,
							id: uploadResult.id,
							uploading: false
						}
					: img
			)

			if (!existingId) {
				onUploadComplete({
					id: uploadResult.id,
					url: uploadResult.url,
					file,
					uploading: false,
					deleteUrl: uploadResult.deleteUrl,
					previewUrl
				})

				revokePreviewUrl(previewUrl)
				previewUrls.delete(tempId)
			}
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Upload failed'

			uploadedImages = uploadedImages.map((img) =>
				img.id === tempId
					? {
							...img,
							uploading: false,
							error: errorMessage
						}
					: img
			)

			if (!existingId) {
				onImageRemove(file.name, previewUrl)
				revokePreviewUrl(previewUrl)
				previewUrls.delete(tempId)
			}

			onUploadError(errorMessage, file.name)
		}
	}

	export function cleanup() {
		previewUrls.forEach((url) => revokePreviewUrl(url))
		previewUrls.clear()
	}
</script>

{#if enabled}
	<input
		bind:this={fileInputRef}
		type="file"
		accept={acceptedTypes.join(',')}
		multiple
		class="hidden"
		onchange={handleFileChange}
	/>
{/if}

<style>
	.hidden {
		@apply sr-only;
	}
</style>
